import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetEmployeePreferredLanguage } from '@/queries/useGetEmployeePreferredLanguage';
import {
  PREFERRED_LANGUAGE_OPTIONS,
  type PreferredLanguage,
} from '@/views/settings/core/apiCalls';

/**
 * After login, applies the employee's persisted preferred language to i18n once per session mount.
 * Personal settings and PATCH still update i18n immediately on save.
 */
export function EmployeePreferredLanguageSync() {
  const { i18n } = useTranslation();
  const { data, isSuccess, isFetching } = useGetEmployeePreferredLanguage();
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!isSuccess || isFetching || appliedRef.current) {
      return;
    }

    const preferredLanguage = data?.preferredLanguage;
    if (
      !preferredLanguage ||
      !PREFERRED_LANGUAGE_OPTIONS.includes(preferredLanguage as PreferredLanguage)
    ) {
      appliedRef.current = true;
      return;
    }

    if (i18n.language !== preferredLanguage) {
      void i18n.changeLanguage(preferredLanguage);
    }

    appliedRef.current = true;
  }, [data?.preferredLanguage, i18n, isFetching, isSuccess]);

  return null;
}
